import {Injectable} from '@angular/core';
import {
  addDoc,
  AggregateField,
  AggregateQuerySnapshot,
  collection, collectionGroup,
  collectionSnapshots,
  deleteDoc,
  deleteField,
  doc,
  docSnapshots,
  DocumentData,
  DocumentReference,
  Firestore,
  GeoPoint,
  getCountFromServer,
  orderBy,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc, where,
  writeBatch
} from '@angular/fire/firestore';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {CreatedByModel, EntityType} from 'spotcharge-types';
import {ContextProvider} from '@core/providers';
import {App} from '@capacitor/app';
import {Platform} from '@ionic/angular';
import {environment} from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class FirestoreService {
  user: CreatedByModel;
  appVersion: string;

  constructor(
    private afs: Firestore,
    private contextProvider: ContextProvider,
    private platform: Platform,
  ) {
    if (this.platform.is('capacitor')) {
      App.getInfo().then((appInfo) => {
        console.log('appInfo', appInfo);
        this.appVersion = appInfo.version;
      });
    } else {
      this.appVersion = environment.appVersion;
    }
  }

  /// Firebase Server Timestamp
  get timestamp() {
    return serverTimestamp();
  }

  get deleteField() {
    return deleteField();
  }

  get batch() {
    return writeBatch(this.afs);
  }

  get db() {
    return this.afs;
  }

  // get createDocId() {
  //     return this.afs.createId();
  // }

  col<T>(ref: string, queryFn?: QueryConstraint[]): Observable<QueryDocumentSnapshot<DocumentData>[]> {
    // return typeof ref === 'string' ? collection(this.afs, ref) : ref;
    if (queryFn) {
      const q = query(collection(this.afs, ref), ...queryFn);
      return collectionSnapshots(q);
    }
    return collectionSnapshots(collection(this.afs, ref));
  }

  colGroup<T>(ref: string, queryFn?: QueryConstraint[]): Observable<QueryDocumentSnapshot<DocumentData>[]> {
    // return typeof ref === 'string' ? collection(this.afs, ref) : ref;
    if (queryFn) {
      const q = query(collectionGroup(this.afs, ref), ...queryFn);
      return collectionSnapshots(q);
    }
    return collectionSnapshots(collectionGroup(this.afs, ref));
  }

  doc<T>(ref: string): DocumentReference {
    return doc(this.afs, ref);
  }

  count(ref: string, queryFn?: QueryConstraint[]): Promise<AggregateQuerySnapshot<{ count: AggregateField<number> }>> {
    if (queryFn) {
      const q = query(collection(this.afs, ref), ...queryFn);
      return getCountFromServer(q);
    }
    return getCountFromServer(collection(this.afs, ref));
  }

  addMetaData(data: any) {
    this.user = this.contextProvider.getUser();
    this.user = this.contextProvider.getUser();
    console.log('created by', this.user);
    console.log('appVersion', this.appVersion);

    return {
      source: EntityType.client,
      updatedAt: this.timestamp,
      createdAt: this.timestamp,
      createdBy: this.user ?? null,
      updatedBy: this.user ?? null,
      deletedAt: null,
      isDeleted: false,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? null,
      appVersion: this.appVersion,
      ...data,
    };
  }

  updateMetaData(data: any) {
    this.user = this.contextProvider.getUser();
    console.log('updated by', this.user);
    return {
      ...data,
      source: EntityType.client,
      updatedAt: this.timestamp,
      updatedBy: this.user ?? null,
      appVersion: this.appVersion,
    };
  }

  deleteMetaData(data?: any) {
    this.user = this.contextProvider.getUser();
    console.log('deleted by', this.user);
    return {
      ...data,
      source: EntityType.client,
      deletedBy: this.user,
      deletedAt: this.timestamp,
      isDeleted: true,
      appVersion: this.appVersion,
    };
  }

  /// **************
  /// Get Data
  /// **************

  doc$<T>(ref: string): Observable<T | null> {
    return docSnapshots(this.doc(ref))
      .pipe(
        map((docSnap) => {
          const data = docSnap.data() as T;
          const id = docSnap.id;
          return data ? {id, ...data} : null;
        }),
      );
  }

  col$<T>(ref: string, queryFn?: QueryConstraint[]): Observable<T[]> {
    return this.col(ref, queryFn)
      .pipe(
        map((docs) => docs.map((a) => a.data()) as T[]),
      );
  }

  colWithIds$<T extends Record<string, unknown>>(ref: string, ...queryFn: QueryConstraint[]): Observable<any[]> {
    return this.col(ref, queryFn)
      .pipe(
        map((actions) => actions.map((a) => {
          const data: Record<string, unknown> = a.data() as T;
          const id = a.id;
          return {id, ...data};
        })),
      );
  }

  collectionGroup$(ref: string, ...queryFn: QueryConstraint[]): Observable<any[]> {
    return this.colGroup(ref, queryFn).pipe(
      map((actions) => actions.map((a) => {
        const data: Record<string, unknown> = a.data() as any;
        const id = a.id;
        return {id, ...data};
      }))
    );
  }

  /// **************
  /// Write Data
  /// **************


  set<T>(ref: string, data: any): Promise<void> {
    const setData = this.updateMetaData(data) as any;
    return setDoc(this.doc(ref), setData, {merge: true});
  }

  update<T>(ref: string, data: any): Promise<void> {
    const updateData = this.updateMetaData(data) as any;
    return updateDoc(this.doc(ref), updateData);
  }

  delete<T>(ref: string, data?: any): Promise<void> {
    const deleteData = this.deleteMetaData(data) as any;
    return updateDoc(this.doc(ref), deleteData);
  }

  deleteRecord<T>(ref: string): Promise<void> {
    return deleteDoc(this.doc(ref));
  }

  add<T>(ref: string, data: any): Promise<DocumentReference> {
    const addData = this.addMetaData(data) as any;
    return addDoc(collection(this.afs, ref), addData);
  }

  geoPoint(lat: number, lng: number): GeoPoint {
    return new GeoPoint(lat, lng);
  }

  async docCount<T>(ref: string, ...queryFn: QueryConstraint[]): Promise<any> {
    return (await this.count(ref, queryFn)).data().count;
  }
}
